import { tokens } from "lib/colors"
import { Fragment, useContext, useEffect, useState } from "react"
import Link from "next/link"
import Icons from "lib/components/Icons"
import Text from "lib/components/Text"
import { useRouter } from "next/router"
import { LanguageContext } from "context/LanguageContext"
import { handleChar } from "lib/helpers"
import useGhostTags from "lib/hooks/useGhostTags"

export interface itemNavigationSidebar {
  url?: string
  name: string
  icon?: string
  tourName?: string
  iconOnly?: boolean
  withIcon?: boolean
  childItem?: {
    name: string
    url: string
  }[]
  categories?,
  toggleDrawer,
  defaultShow?: boolean
  slug?: string
}

interface WrapperItemSidebarProps {
  children: React.ReactNode
  anyChildItem: boolean
  url: string
  pathName: string
  showChildren: boolean
  setShowChildren: () => void
  dataChildren: any
  toggleDrawer,
  t,
  setSelectedCategory,
  setSelectedParentCategory,
  slug?: string
}

function NavigationChildrenSidebar({ dataChildren, pathName, showChildren, toggleDrawer, t, setSelectedCategory, setSelectedParentCategory, slug = "insight" }) {
  return (
    <div
      className={`w-full transition-height duration-300 transform ${
        showChildren ? `visible ${slug === "insight" ? "h-[304px]" : "h-[180px]"} overflow-y-auto` : "invisible h-0"
      } bg-neutral-20`}>
      <Fragment>
        <div onClick={() => toggleDrawer()} role="button" tabIndex={0}>
          <Link href={`/blog?type=${slug}`} legacyBehavior>
            <div
              className="hover:text-primary py-3 px-2.5 rounded-md text-default">
              <div className="relative flex">
                <Text customClass="text-[14px]">
                  {t("all_categories")}
                </Text>
              </div>
            </div>
          </Link>
        </div>
      </Fragment>
      {dataChildren.map((itemChildren, indexItemChildren) => (
        <Fragment key={indexItemChildren}>
          <div onClick={() => {
              if (itemChildren.categories.length > 0) {
                setSelectedParentCategory(itemChildren.category_name)
                setSelectedCategory(itemChildren.categories)
                const drawer = document.querySelector("[class^=\"EZDrawer__container\"]") as HTMLElement
                if (drawer) {
                  drawer.style.overflow = "hidden"
                }
                
              } else {
                setSelectedParentCategory(null)
                setSelectedCategory([])
                toggleDrawer()
              }
            }} role="button" tabIndex={0}>
            <Link href={`/blog?type=${slug}&tag=${itemChildren.slug}`} legacyBehavior>
              <div
                className={`hover:text-primary py-3 px-2.5 rounded-md ${
                  itemChildren.slug === pathName
                    ? "bg-primary text-white hover:bg-transparent"
                    : "text-default"
                }`}>
                <div className="relative flex">
                  <Text customClass="text-[14px]">
                    {itemChildren.category_name}
                  </Text>
                  {itemChildren.categories.length > 0 && (
                    <div role="button" tabIndex={0} className="absolute right-0">
                      <Icons
                        iconName="arrow-right"
                        width="24"
                        height="24"
                      />
                    </div>
                  )}
                </div>
              </div>
            </Link>
          </div>
        </Fragment>
      ))}
    </div>
  )
}

function WrapperItemSidebarBlog({
  children,
  anyChildItem = false,
  url = "#",
  pathName = "/",
  showChildren = false,
  dataChildren = [],
  setShowChildren,
  toggleDrawer,
  t,
  setSelectedCategory,
  setSelectedParentCategory,
  slug = "insight",
}: WrapperItemSidebarProps) {
  if (anyChildItem) {
    return (
      <>
        <div
          aria-label="item"
          role="button"
          tabIndex={0}
          className="w-full"
          onClick={setShowChildren}>
          {children}
        </div>

        <NavigationChildrenSidebar
          dataChildren={dataChildren}
          showChildren={showChildren}
          pathName={pathName}
          toggleDrawer={toggleDrawer}
          setSelectedCategory={setSelectedCategory}
          setSelectedParentCategory={setSelectedParentCategory}
          t={t}
          slug={slug}
        />
      </>
    )
  }

  return (
    <div>
      {children}
    </div>
  )
}

function NavigationItemSidebarBlog({ url, icon, name, tourName, iconOnly = false, childItem, withIcon = true, toggleDrawer, defaultShow = false, categories, slug = "insight" }: itemNavigationSidebar) {
  const { useTranslation: t } = useContext(LanguageContext)
  const router = useRouter()
  const [showChildren, setShowChildren] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState([])
  const [selectedParentCategory, setSelectedParentCategory] = useState(null)
  let isActiveItem = false
  const categoryData = categories.filter((category) => !category.parent_id)
  const categoryBlog = useGhostTags()

  useEffect(() => {
    const itemChildren = childItem.find((item) => item.url === router.pathname)
    if (itemChildren && itemChildren.url) {
      setShowChildren(true)
    }
  }, [childItem, router])

  if (router.pathname === url) {
    isActiveItem = true
  }

  return (
    <div>
      <div className="relative flex items-center flex-col">
        <WrapperItemSidebarBlog
          anyChildItem={categoryData && categoryData.length > 0}
          url={url}
          showChildren={showChildren}
          dataChildren={categoryData}
          pathName={router.pathname}
          toggleDrawer={toggleDrawer}
          slug={slug}
          t={t}
          setSelectedCategory={setSelectedCategory}
          setSelectedParentCategory={setSelectedParentCategory}
          setShowChildren={() => {
            setShowChildren((currentItem) => !currentItem)
          }}>
          <div
            className={`hover:text-primary py-3 sm:mx-4 rounded-md navItem ${tourName} ${
              isActiveItem
                ? "bg-primary text-white hover:bg-transparent"
                : "text-default"
            }`}>
            <div className="flex relative items-justify-between items-center">
              <div>
                <Text>
                  {t(`${handleChar({ value: name, replacement: "_" }).toLowerCase()}`)}
                </Text>
              </div>
              {categoryData && categoryData.length > 0 && (
                <div className="absolute right-0">
                  {!iconOnly && (
                    <Icons
                      iconName="arrow-right"
                      width="24"
                      height="24"
                      styles={`transform ${
                        showChildren ? "rotate-90 duration-500" : "rotate-0 duration-500"
                      }`}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </WrapperItemSidebarBlog>
      </div>
      {selectedCategory.length > 0 && 
        <div className="absolute w-full h-full inset-0 bg-white text-black rounded-md">
          <div className="p-2">
            <Icons iconName="arrow-left" width="24" height="24" styles="cursor-pointer" color={tokens.primary.DEFAULT} onClick={() => {
              setSelectedCategory([])
              setSelectedParentCategory(null)
              const drawer = document.querySelector("[class^=\"EZDrawer__container\"]") as HTMLElement
              if (drawer) {
                drawer.style.overflow = "scroll"
              }
            }} />
          </div>
          <hr />
          <div className="px-2 py-2">
            <Text customClass="text-[12px] font-semibold">
              {`${selectedParentCategory} ${t("article_options").toLowerCase()}`}
            </Text>
          </div>
          <div className="px-2">
            {selectedCategory.map((category) => (
              <div role="button" tabIndex={0} key={category.id} className="py-1.5 flex justify-between items-center" onClick={() => {
                setSelectedCategory([])
                const drawer = document.querySelector("[class^=\"EZDrawer__container\"]") as HTMLElement
                if (drawer) {
                  drawer.style.overflow = "scroll"
                }
                toggleDrawer()
                router.push(`/blog?type=insight&tag=${category.slug}`)
              }}>
                <Text customClass="text-[12px]">
                  {`${category.category_name} (${categoryBlog.find((tag) => tag.slug === category.slug)?.count?.posts || 0})`}
                </Text>
                <Icons
                  iconName="arrow-right"
                  width="24"
                  height="24"
                />
              </div>
            ))}
          </div>
        </div>
      }
    </div>
  )
}

NavigationItemSidebarBlog.defaultProps = {
  childItem: [],
}

export default NavigationItemSidebarBlog
